import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { getCurrentUser } from 'aws-amplify/auth';
import { tap, catchError } from 'rxjs/operators';
import { TOASTER_MSG, USER_ROLE } from 'src/app/app-constants';
import { FormStateModel } from 'src/app/form.state.model';
import { OrgModel } from 'src/app/models/org.model';

import { CountriesModel } from '../../../models/countries.model';
import { CustomerModel } from '../../../models/customer.model';
import { DelivererModel } from '../../../models/deliverer.model';
import { DiscountModel } from '../../../models/discount.model';
import { MembersModel } from '../../../models/members.model';
import { StatesAndCitiesModel } from '../../../models/states.model';
import { OrgState } from '../org';

import { MembersAction } from './members.action';
import { MembersService } from './members.service';
import { MembersStateModel } from './members.state.model';

@State<MembersStateModel>({
    name: 'members',
    defaults: new MembersStateModel(),
})
@Injectable()
export class MembersState {
    constructor(
        private membersService: MembersService,
        private snackBar: MatSnackBar,
        private store: Store
    ) {}

    @Selector()
    static currentUser(state: MembersStateModel) {
        return state.currentUser;
    }

    @Selector()
    static addFormErrors(state: MembersStateModel) {
        return state.addForm.errors;
    }

    @Selector()
    static membersList(state: MembersStateModel) {
        return state.membersList;
    }

    @Selector()
    static memberRoles(state: MembersStateModel) {
        return state.memberRoles;
    }

    @Selector()
    static selectedMember(state: MembersStateModel) {
        return state.selectedMember;
    }

    @Selector()
    static customerFormError(state: MembersStateModel) {
        return state.addCustomerForm.errors;
    }

    @Selector()
    static customersList(state: MembersStateModel) {
        return state.customersList;
    }

    @Selector()
    static delivererFormError(state: MembersStateModel) {
        return state.addDelivererForm.errors;
    }

    @Selector()
    static deliverersList(state: MembersStateModel) {
        return state.delivererslist;
    }

    @Selector()
    static countriesList(state: MembersStateModel) {
        return state.countriesList;
    }

    @Selector()
    static statesList(state: MembersStateModel) {
        return state.statesList;
    }

    @Selector()
    static citiesList(state: MembersStateModel) {
        return state.citiesList;
    }

    @Selector()
    static discountsList(state: MembersStateModel) {
        return state.discountList;
    }

    @Selector()
    static discountFormError(state: MembersStateModel) {
        return state.addDiscountForm.errors;
    }

    @Selector()
    static serviceAreasList(state: MembersStateModel) {
        return state.serviceAreasList;
    }

    @Action(MembersAction.Add)
    add(ctx: StateContext<MembersStateModel>, action: MembersAction.Add) {
        const { addForm } = ctx.getState();
        const { membersList } = ctx.getState();
        const addMember = action.payload;
        if (!addMember?.id) {
            return this.membersService.add(addMember, this.organization().id).pipe(
                tap((resp: MembersModel) => {
                    this.snackBar.open(TOASTER_MSG.general.addstaff.display);
                    const updatedMembersList = [...membersList, resp];
                    ctx.patchState({
                        membersList: updatedMembersList,
                        addForm: {
                            model: {
                                ...addForm.model,
                                name: '',
                                role_id: null,
                                email: '',
                                contact_number: '',
                                cnic: '',
                            },
                            dirty: false,
                            status: '',
                            errors: null,
                        },
                    });
                }),
                catchError((err) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addForm: {
                            ...state.addForm,
                            errors: this.checkErrors(err),
                        },
                    });
                    throw err;
                })
            );
        } else if (addForm.model['id']) {
            return this.membersService.updateStaff(addForm.model).pipe(
                tap((resp: MembersModel) => {
                    this.snackBar.open(TOASTER_MSG.general.update.display);
                    const state = ctx.getState();
                    const index = state.membersList.findIndex((item) => {
                        return item.id === resp.id;
                    });

                    const newMemberList = [...state.membersList];
                    newMemberList.splice(index, 1, resp);
                    ctx.patchState({
                        membersList: newMemberList,
                    });
                }),
                catchError((err) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addForm: {
                            ...state.addForm,
                            errors: err,
                        },
                    });
                    throw err;
                })
            );
        }
    }

    @Action(MembersAction.GetAll)
    getAll(ctx: StateContext<MembersStateModel>) {
        return this.membersService.getAll(this.organization().id).pipe(
            tap((resp) => {
                ctx.patchState({
                    membersList: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.DeleteStaff)
    deleteStaff(ctx: StateContext<MembersStateModel>, action: MembersAction.DeleteStaff) {
        const { membersList } = ctx.getState();
        return this.membersService.deleteStaff(action.payload).pipe(
            tap((resp) => {
                const index = membersList.findIndex((item) => {
                    return item.id === action.payload.id;
                });
                membersList[index].delete();
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.GetRoles)
    getRoles(ctx: StateContext<MembersStateModel>, action: MembersAction.GetRoles) {
        return this.membersService.getRoles(action.orgId).pipe(
            tap((resp) => {
                ctx.patchState({
                    memberRoles: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.ResetPin)
    resetPin(ctx: StateContext<MembersStateModel>, action: MembersAction.ResetPin) {
        const memberId = action.memberId;
        const membersList = ctx.getState().membersList;
        return this.membersService.resetPin(memberId).pipe(
            tap((resp) => {
                this.snackBar.open(TOASTER_MSG.general.update.display);
                ctx.patchState({
                    membersList: membersList.map((item, index) =>
                        item.id === resp.id ? (membersList[index] = resp) : item
                    ),
                    selectedMember: resp,
                });
            }),
            catchError((error) => {
                throw error;
            })
        );
    }

    @Action(MembersAction.SelectedMember)
    selectedMember(ctx: StateContext<MembersStateModel>, action: MembersAction.SelectedMember) {
        return ctx.patchState({
            selectedMember: action.member,
        });
    }

    @Action(MembersAction.ClearMemberForm)
    clearMemberForm(ctx: StateContext<MembersStateModel>) {
        return ctx.patchState({
            addForm: new FormStateModel(),
        });
    }

    @Action(MembersAction.AddCustomer)
    addCustomer(ctx: StateContext<MembersStateModel>) {
        const { addCustomerForm } = ctx.getState();
        const { customersList } = ctx.getState();
        if (!addCustomerForm.model['id']) {
            return this.membersService.addCustomer(addCustomerForm.model).pipe(
                tap((resp) => {
                    this.snackBar.open(TOASTER_MSG.general.add.display);
                    customersList.push(resp);
                    ctx.patchState({
                        customersList,
                    });
                }),
                catchError((err) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addCustomerForm: {
                            ...state.addCustomerForm,
                            errors: err,
                        },
                    });
                    throw err;
                })
            );
        } else if (addCustomerForm.model['id']) {
            return this.membersService.updateCustomer(addCustomerForm.model).pipe(
                tap((resp: CustomerModel) => {
                    const state = ctx.getState();
                    const index = state.customersList.findIndex((customer) => {
                        return customer.id === resp.id;
                    });

                    const newCustomerList = [...state.customersList];
                    newCustomerList.splice(index, 1, resp);
                    ctx.patchState({
                        customersList: newCustomerList,
                    });
                    this.snackBar.open(TOASTER_MSG.general.update.display);
                }),
                catchError((err) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addCustomerForm: {
                            ...state.addCustomerForm,
                            errors: err,
                        },
                    });
                    throw err;
                })
            );
        }
    }

    @Action(MembersAction.GetCustomers)
    getCustomers(ctx: StateContext<MembersStateModel>) {
        return this.membersService.getCustomers(this.organization().id).pipe(
            tap((resp) => {
                ctx.patchState({
                    customersList: [...ctx.getState().customersList, ...resp],
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.ResetCustomer)
    resetCustomer(ctx: StateContext<MembersStateModel>) {
        ctx.patchState({
            customersList: [],
        });
    }

    @Action(MembersAction.DeleteCustomer)
    deleteCustomer(ctx: StateContext<MembersStateModel>, action: MembersAction.DeleteCustomer) {
        let { customersList } = ctx.getState();
        if (action?.payload) {
            return this.membersService.deleteCustomer(action.payload).pipe(
                tap((resp) => {
                    customersList = customersList.filter((customer) => customer.id !== action.payload.id);
                    ctx.patchState({
                        customersList,
                    });
                }),
                catchError((err) => {
                    throw err;
                })
            );
        }
    }

    @Action(MembersAction.AddDeliverer)
    addDeliverer(ctx: StateContext<MembersStateModel>) {
        const { addDelivererForm } = ctx.getState();
        const { delivererslist } = ctx.getState();
        if (!addDelivererForm.model['id']) {
            return this.membersService.addDeliverer(addDelivererForm.model).pipe(
                tap((resp) => {
                    this.snackBar.open(TOASTER_MSG.general.add.display);
                    delivererslist.push(resp);
                    ctx.patchState({
                        delivererslist,
                    });
                }),
                catchError((err) => {
                    if (err.contacts) {
                        err = {
                            ...err,
                            contacts: Array.isArray(err.contacts) && err.contacts[0]?.contact_number,
                        };
                    }
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addDelivererForm: {
                            ...state.addDelivererForm,
                            errors: err,
                        },
                    });
                    throw err;
                })
            );
        } else if (addDelivererForm.model['id']) {
            return this.membersService.updateDeliverer(addDelivererForm.model).pipe(
                tap((resp: DelivererModel) => {
                    const state = ctx.getState();
                    const index = state.delivererslist.findIndex((deliverer) => {
                        return deliverer.id === resp.id;
                    });

                    const newDelivererList = [...state.delivererslist];
                    newDelivererList.splice(index, 1, resp);
                    ctx.patchState({
                        delivererslist: newDelivererList,
                    });
                    this.snackBar.open(TOASTER_MSG.general.update.display);
                }),
                catchError((err) => {
                    if (err.contacts) {
                        err = {
                            ...err,
                            contacts: Array.isArray(err.contacts) && err.contacts[0]?.contact_number,
                        };
                    }
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addDelivererForm: {
                            ...state.addDelivererForm,
                            errors: err,
                        },
                    });
                    throw err;
                })
            );
        }
    }

    @Action(MembersAction.GetDeliverers)
    getDeliverers(ctx: StateContext<MembersStateModel>) {
        return this.membersService.getDeliverers(this.organization().id).pipe(
            tap((resp) => {
                ctx.patchState({
                    delivererslist: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.DeleteDeliverer)
    deleteDeliverer(ctx: StateContext<MembersStateModel>, action: MembersAction.DeleteDeliverer) {
        let { delivererslist } = ctx.getState();
        return this.membersService.deleteDeliverer(action.payload).pipe(
            tap((resp) => {
                delivererslist = delivererslist.filter((deliverer) => deliverer.id !== action.payload.id);
                ctx.patchState({
                    delivererslist,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.GetCountries)
    getCountries(ctx: StateContext<MembersStateModel>) {
        return this.membersService.getCountries().pipe(
            tap((resp: CountriesModel[]) => {
                ctx.patchState({
                    countriesList: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.GetStates)
    getStates(ctx: StateContext<MembersStateModel>, action: MembersAction.GetStates) {
        const country_id = action.payload;
        return this.membersService.getStates(country_id).pipe(
            tap((resp: StatesAndCitiesModel[]) => {
                ctx.patchState({
                    statesList: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.GetCities)
    getCities(ctx: StateContext<MembersStateModel>, action: MembersAction.GetCities) {
        const state_id = action.payload;
        return this.membersService.getCities(state_id).pipe(
            tap((resp: StatesAndCitiesModel[]) => {
                ctx.patchState({
                    citiesList: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.AddDiscount)
    addDiscount(ctx: StateContext<MembersStateModel>) {
        const { addDiscountForm, discountList } = ctx.getState();
        if (!addDiscountForm.model['id']) {
            return this.membersService.addDiscount(addDiscountForm.model).pipe(
                tap((resp: DiscountModel) => {
                    const existingDiscount = discountList.findIndex(
                        (discount: DiscountModel) => discount.staff_name?.trim() === resp.staff_name?.trim()
                    );
                    const newDiscountList = [...discountList];
                    existingDiscount !== -1 ? (newDiscountList[existingDiscount] = resp) : newDiscountList.push(resp);
                    ctx.patchState({
                        discountList: newDiscountList,
                        addDiscountForm: {
                            ...addDiscountForm,
                            model: {
                                ...addDiscountForm.model,
                                id: '',
                                staff_id: '',
                                rate: 0,
                            },
                        },
                    });
                }),
                catchError((err) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addDiscountForm: {
                            ...state.addDiscountForm,
                            errors: err,
                        },
                    });
                    throw err;
                })
            );
        } else if (addDiscountForm.model['id']) {
            return this.membersService.updateDiscount(addDiscountForm.model).pipe(
                tap((resp: DiscountModel) => {
                    const state = ctx.getState();
                    const index = state.discountList.findIndex((staff) => {
                        return staff.id === resp.id;
                    });

                    const newDiscountList = [...state.discountList];
                    newDiscountList.splice(index, 1, resp);
                    ctx.patchState({
                        discountList: newDiscountList,
                    });
                }),
                catchError((err) => {
                    const state = ctx.getState();
                    ctx.setState({
                        ...state,
                        addDiscountForm: {
                            ...state.addDiscountForm,
                            errors: err,
                        },
                    });
                    throw err;
                })
            );
        }
    }

    @Action(MembersAction.GetDiscount)
    getDiscount(ctx: StateContext<MembersStateModel>) {
        return this.membersService.getDiscounts(this.organization().id).pipe(
            tap((resp) => {
                ctx.patchState({
                    discountList: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.DeleteDiscount)
    deleteDiscount(ctx: StateContext<MembersStateModel>, action: MembersAction.DeleteDiscount) {
        let { discountList } = ctx.getState();
        return this.membersService.deleteDiscount(action.payload).pipe(
            tap((resp) => {
                discountList = discountList.filter((discount) => discount.id !== action.payload.id);
                ctx.patchState({
                    discountList,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.GetServiceAreas)
    getServiceAreas(ctx: StateContext<MembersStateModel>) {
        return this.membersService.getServiceAreas(this.organization().id).pipe(
            tap((resp) => {
                ctx.patchState({
                    serviceAreasList: resp,
                });
            }),
            catchError((err) => {
                throw err;
            })
        );
    }

    @Action(MembersAction.CurrentUser)
    currentUser(ctx: StateContext<MembersStateModel>) {
        return getCurrentUser().then((user) => {
            ctx.patchState({
                currentUser: user,
            });
        });
    }

    organization(): OrgModel {
        return this.store.selectSnapshot(OrgState.org);
    }

    checkErrors(err) {
        if (err.userrole_id) {
            return { ...err, userrole_id: err.userrole_id };
        }
        return err;
    }
}
